import { createApp } from 'vue';
import './utils/clientLogger.js';
import App from './App.vue';
import router from './router';
import { install as VueMonacoEditorPlugin } from '@guolao/vue-monaco-editor';
import { setupMobileAutoScroll } from './utils/mobileAutoScroll.js';
import { installCsrfFetch } from './services/csrfFetch.js';

// Silence benign Monaco cancellation errors that appear as unhandled promise rejections
// during editor disposal or route changes.
window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = typeof reason === 'string' ? reason : reason?.message;
    const name = reason?.name;
    const isMonacoCancellation =
        message === 'Canceled' ||
        message === 'canceled' ||
        name === 'Canceled' ||
        (window.monaco &&
            window.monaco.CancellationError &&
            typeof window.monaco.CancellationError.isCancellationError === 'function' &&
            window.monaco.CancellationError.isCancellationError(reason));
    if (isMonacoCancellation) {
        event.preventDefault();
    }
});

import Aura from '@primeuix/themes/aura';
import PrimeVue from 'primevue/config';
import '@primevue/core/base/style';
// PrimeVue component styles
import 'primevue/button/style';
import 'primevue/select/style';
import 'primevue/inputotp/style';
import 'primevue/inputtext/style';
import 'primevue/dialog/style';
import 'primevue/datatable/style';
import 'primevue/column/style';
import 'primevue/fileupload/style';
import 'primevue/progressbar/style';
import 'primevue/progressspinner/style';
import 'primevue/message/style';
import 'primevue/confirmdialog/style';
import 'primevue/tag/style';
import 'primevue/toast/style';
import 'primevue/card/style';
import 'primevue/selectbutton/style';
import 'primevue/calendar/style';
import 'primevue/datepicker/style';
import 'primevue/treeselect/style';
import 'primevue/toggleswitch/style';
import 'primevue/tooltip/style';
import InputOtp from 'primevue/inputotp';
import Button from 'primevue/button';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import Tooltip from 'primevue/tooltip';
import DatePicker from 'primevue/datepicker';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { initServerStatus } from '@/composables/serverStatus';
import CustomDropdown from '@/components/CustomDropdown.vue';
import CustomInput from '@/components/CustomInput.vue';

import 'primeicons/primeicons.css';

setupMobileAutoScroll();
installCsrfFetch();

// Set CSS custom property for viewport height on mobile devices
function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
}

// Set initial viewport height
setViewportHeight();

// Update viewport height on resize and orientation change
window.addEventListener('resize', setViewportHeight);
window.addEventListener('orientationchange', () => {
    // Small delay to ensure the viewport has updated
    setTimeout(setViewportHeight, 100);
});

// Remove autofocus attributes from elements other than the active element to suppress
// the console message: "Autofocus processing was blocked because a document already has a focused element."
function removeConflictingAutofocus() {
    try {
        const active = document.activeElement;
        if (active && active !== document.body) {
            document.querySelectorAll('[autofocus]').forEach((el) => {
                if (el !== active) el.removeAttribute('autofocus');
            });
        }
    } catch {
        // ignore any DOM access errors during SSR or unusual environments
    }
}

const app = createApp(App);

app.component('InputOtp', InputOtp);
app.component('Button', Button);
app.component('Select', Select);
app.component('ToggleSwitch', ToggleSwitch);
app.component('DatePicker', DatePicker);
app.component('CustomDropdown', CustomDropdown);
app.component('CustomInput', CustomInput);
app.directive('tooltip', Tooltip.extend('tooltip', {
    methods: {
        show(el, options, showDelay) {
            // Repeated enter events must not append duplicate overlays with the same id.
            clearTimeout(this.timer);
            const showOnce = () => {
                if (!this.getTooltipElement(el)) this.tooltipActions(el, options);
            };
            if (showDelay === undefined) showOnce();
            else this.timer = setTimeout(showOnce, showDelay);
        }
    }
}));
app.use(router);
const monacoOptions = {};
if (!(window.monaco && window.require)) {
    monacoOptions.paths = { vs: '/monaco-editor' };
}
app.use(VueMonacoEditorPlugin, monacoOptions);
app.use(PrimeVue, {
    theme: {
        preset: Aura,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);

try {
    initServerStatus();
    app.mount('#app');
    performance.mark('corecry-app-mounted');
    removeConflictingAutofocus();
    router.afterEach(() => setTimeout(removeConflictingAutofocus, 50));
} catch (error) {
    globalThis.clientLogger?.error('Error during app initialization:', error);
}
